import classnames from "classnames";
import { format } from "date-fns";

import { getDateFnsLocale } from "@/lib/date-fns-locale";
import { useLocale, useTranslation } from "@/lib/i18n";
import EarlyWarningTableHeaderDate from "@/features/shared/widgets/early-warning-table/early-warning-table-header";

import { collapseTableItems, itemsForPhenomenon } from "./cell-label";
import { RiskPictureCell } from "./risk-picture-cell";
import { RiskPictureMapRow } from "./risk-picture-map-row";
import type { RiskPictureModel } from "./risk-picture-model";
import {
  riskPicturePhenomenonIconUrl,
  riskPicturePhenomenonName,
} from "./risk-picture-phenomena";
import styles from "./risk-picture.module.css";

interface RiskPictureTableProps {
  model: RiskPictureModel;
  selectedDay: number;
  onSelectDay: (index: number) => void;
}

export const RiskPictureTable = ({
  model,
  selectedDay,
  onSelectDay,
}: RiskPictureTableProps) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const dateFnsLocale = getDateFnsLocale(locale);

  return (
    <table className={styles.table}>
      <colgroup>
        <col className={styles.phenomenonColumn} />
        {model.days.map((day) => (
          <col key={day.date} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th />
          {model.days.map((day, index) => (
            <th
              key={day.date}
              className={classnames(
                styles.dayHeader,
                index === selectedDay && styles.dayHeaderSelected,
              )}
              onClick={() => onSelectDay(index)}
            >
              <div className={styles.dayHeaderContent}>
                {index === 0 ? (
                  <span>
                    {t("general.today")}{" "}
                    <span className={styles.dayHeaderWeekday}>
                      {format(new Date(day.date), "eeeeee", { locale: dateFnsLocale })}
                    </span>{" "}
                    {format(new Date(day.date), "d", { locale: dateFnsLocale })}{" "}
                    <span className={styles.dayHeaderMonth}>
                      {format(new Date(day.date), "MMM", { locale: dateFnsLocale })}
                    </span>
                  </span>
                ) : (
                  <EarlyWarningTableHeaderDate
                    date={day.date}
                    narrow={false}
                    onClick={() => onSelectDay(index)}
                  />
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {model.phenomena.map((phenomenon) => (
          <tr key={phenomenon} className={styles.phenomenonRow}>
            <th scope="row">
              <div className={styles.phenomenon}>
                <span>{riskPicturePhenomenonName(phenomenon, t)}</span>
                <span className={styles.phenomenonIcon}>
                  <span
                    style={{
                      maskImage: `url("${riskPicturePhenomenonIconUrl(phenomenon)}")`,
                      WebkitMaskImage: `url("${riskPicturePhenomenonIconUrl(phenomenon)}")`,
                    }}
                  />
                </span>
              </div>
            </th>
            {model.days.map((day, index) => (
              <td
                key={day.date}
                className={classnames(
                  styles.dayCell,
                  index === selectedDay && styles.dayCellSelected,
                )}
                onClick={() => onSelectDay(index)}
              >
                <div className={styles.dayCellItems}>
                  {collapseTableItems(itemsForPhenomenon(day.items, phenomenon)).map(
                    (item) => (
                      <RiskPictureCell
                        key={item.id}
                        item={item}
                        onSelect={() => onSelectDay(index)}
                      />
                    ),
                  )}
                </div>
              </td>
            ))}
          </tr>
        ))}
        <RiskPictureMapRow
          model={model}
          selectedDay={selectedDay}
          onSelectDay={onSelectDay}
        />
      </tbody>
    </table>
  );
};
